import {ITransactionState} from "../types";
import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {toAmount} from "../summary";
import {adminRequest, buildQuery} from "../../api/adminApi";

const initialState: ITransactionState = {
    transactions: null,
    lencoTransactions: null,
    loading: false,
    error: undefined,
    pageInfo: {
        pageNumber: 0,
        pageSize: 0,
        totalItems: 0,
        totalPages: 0
    },
    summary: {
        totalCount: 0,
        totalAmount: 0,
        successfulCount: 0,
        failedCount: 0,
        pendingCount: 0,
        lencoTotalCount: 0,
        lencoTotalAmount: 0
    }
};

export const getTransactions = createAsyncThunk<any, {
    token: string,
    searchParam: string,
    pageNumber: number
}>("getTransactions", async (data) => {
    return adminRequest(`/payment/transactions${buildQuery({
        searchParam: data.searchParam,
        pageSize: 10,
        pageNumber: data.pageNumber
    })}`, {token: data.token});
});

export const getLencoTransactions = createAsyncThunk<any, {
    token: string,
    searchParam: string,
    pageNumber: number
}>("getLencoTransactions", async (data) => {
    return adminRequest(`/payment/lenco-transaction${buildQuery({
        searchParam: data.searchParam,
        pageNumber: data.pageNumber
    })}`, {token: data.token});
});

const transactionSlice = createSlice({
    name: 'transaction',
    initialState,
    reducers: {
        reset: (state) => {
            state.transactions = null;
            state.lencoTransactions = null;
            state.loading = false;
            state.error = undefined;
            state.pageInfo = {
                pageNumber: 0,
                pageSize: 0,
                totalItems: 0,
                totalPages: 0
            };
            state.summary = {
                totalCount: 0,
                totalAmount: 0,
                successfulCount: 0,
                failedCount: 0,
                pendingCount: 0,
                lencoTotalCount: 0,
                lencoTotalAmount: 0
            };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getTransactions.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(getTransactions.fulfilled, (state, action) => {
                if (!action.payload) return;

                if (action.payload.isSuccessfull === false) {
                    state.loading = false;
                    state.error = action.payload.message;
                    return;
                }

                state.loading = false;
                state.error = undefined;
                const transactions = action.payload?.returnedObject?.transactions || [];
                state.transactions = transactions.map((item: any) => {
                    return {...item, key: item.id};
                });
                state.pageInfo = action.payload?.returnedObject?.pageInfo;
                state.summary.totalCount = state.pageInfo?.totalItems || transactions.length;
                state.summary.totalAmount = transactions.reduce((total: number, item: any) => {
                    return total + toAmount(item.transactionAmount ?? item.amount);
                }, 0);
                state.summary.successfulCount = transactions.filter((item: any) => {
                    const status = String(item.transactionStatus || item.status || "").toLowerCase();
                    return status.includes("success") || status.includes("complete");
                }).length;
                state.summary.failedCount = transactions.filter((item: any) => {
                    const status = String(item.transactionStatus || item.status || "").toLowerCase();
                    return status.includes("fail");
                }).length;
                state.summary.pendingCount = transactions.filter((item: any) => {
                    const status = String(item.transactionStatus || item.status || "").toLowerCase();
                    return status.includes("pending") || status.includes("processing");
                }).length;
            })
            .addCase(getLencoTransactions.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(getLencoTransactions.fulfilled, (state, action) => {
                if (!action.payload) return;

                if (action.payload.isSuccessfull === false) {
                    state.loading = false;
                    state.error = action.payload.message;
                    return;
                }

                state.loading = false;
                state.error = undefined;
                const lencoTransactions = action.payload?.transactions || [];
                state.lencoTransactions = lencoTransactions.map((item: any) => {
                    return {
                        ...item,
                        key: item.id,
                        accountName: item.details?.accountName,
                        accountNumber: item.details?.accountNumber,
                        bankName: item.details?.bank?.name,
                    };
                });
                state.pageInfo = {
                    pageNumber: action.payload?.meta?.currentPage,
                    pageSize: action.payload?.meta?.perPage,
                    totalItems: action.payload?.meta?.total,
                    totalPages: action.payload?.meta?.pageCount
                };
                state.summary.lencoTotalCount = state.pageInfo?.totalItems || lencoTransactions.length;
                state.summary.lencoTotalAmount = lencoTransactions.reduce((total: number, item: any) => {
                    return total + toAmount(item.amount);
                }, 0);
            })
            .addCase(getTransactions.rejected, (state, action) => {
                state.error = action.error.message;
                state.loading = false;
            })
            .addCase(getLencoTransactions.rejected, (state, action) => {
                state.error = action.error.message;
                state.loading = false;
            });
    }
});

export const {reset} = transactionSlice.actions;

export default transactionSlice.reducer;
